/**
 * <product-fitment> — the product's vehicle keys, for the garage.
 *
 * Fitment content lives in the product description now (the structured
 * table, docs/product-description-template.html); the keys here are the
 * product's vehicle collections, which the rest of the theme uses for a
 * machine. From them, and the rows of the description's table, this paints:
 *
 *  - the "Guaranteed to fit your …" badge in the details column;
 *  - marks on the description's rows for the machines in the garage — the
 *    active one strong, the rest quiet — which product-description.js then
 *    floats to the top of the table.
 *
 * The badge only ever makes a POSITIVE claim. The collection list is large
 * but not provably complete, so a machine that is absent produces no badge
 * rather than "doesn't fit": a wrong denial costs the sale and the trust, a
 * missing badge costs nothing.
 */
class ProductFitment extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    // A product without fitment (apparel, riding gear): no badge, no marks.
    if (this.hasAttribute('data-no-fitment')) return;

    const json = this.querySelector('[data-fitment-keys]');
    try {
      this.keys = new Set(JSON.parse(json ? json.textContent : '[]'));
    } catch {
      this.keys = new Set();
    }

    const section = this.closest('.shopify-section') ?? document;
    this.section = section;
    this.badge = section.querySelector('[data-fitment-badge]');

    this.onGarageChange = () => this.markMine();
    document.addEventListener('garage:changed', this.onGarageChange);
    this.markMine();
    this.paintBreadcrumb();
  }

  /**
   * Arriving from the garage's machine's own collection, the breadcrumb's
   * trail is that machine (Lucas, 2026-10-12): Home › the machine › the part,
   * linking back to its parts. Any other way in keeps the category trail.
   */
  paintBreadcrumb() {
    const active = window.Garage?.active();
    const nav = document.querySelector('[data-breadcrumb]');
    if (!active || !nav) return;
    let from;
    try { from = new URL(document.referrer); } catch { return; }
    if (from.origin !== window.location.origin) return;
    const path = `/collections/${encodeURIComponent(active.key)}`;
    if (from.pathname !== path && !from.pathname.startsWith(`${path}/`)) return;
    const trail = [...nav.querySelectorAll('[data-breadcrumb-trail]')];
    const item = document.createElement('li');
    item.setAttribute('data-breadcrumb-trail', '');
    item.setAttribute('data-breadcrumb-machine', '');
    const link = document.createElement('a');
    link.href = from.pathname + from.search;
    link.textContent = active.nickname || active.label;
    item.append(link);
    const last = nav.querySelector('[aria-current="page"]');
    if (last) last.before(item); else nav.querySelector('ol')?.append(item);
    for (const old of trail) old.remove();
  }

  disconnectedCallback() {
    document.removeEventListener('garage:changed', this.onGarageChange);
  }

  /** Shopify's handleize: "2015 Polaris RMK 800 155" → "2015-polaris-rmk-800-155". */
  static handleize(text) {
    return String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /** Every fitment row in the description, keyed like a vehicle collection. */
  rows() {
    const rows = this.section.querySelectorAll('[data-fmt-fitment] tbody tr');
    for (const row of rows) {
      if (!row.dataset.fmtKey) {
        row.dataset.fmtKey = ProductFitment.handleize([...row.cells].map((cell) => cell.textContent.trim()).join(' '));
      }
    }
    return [...rows];
  }

  markMine() {
    const active = window.Garage?.active() ?? null;
    let rowFits = false;

    // Only the current machine's row is marked (request 176: the other saved
    // machines used to carry a quiet mark and float up with it).
    for (const row of this.rows()) {
      row.removeAttribute('data-yours');
      row.removeAttribute('data-garage');
      row.removeAttribute('title');
      const key = row.dataset.fmtKey;
      if (active && key === active.key) {
        row.setAttribute('data-yours', '');
        row.title = 'Your current machine';
        rowFits = true;
      }
    }

    // The diagram button is part-diagram.js's: it shows once the Fitment API
    // says the part is on a drawing for the machine.
    this.paintBadge(active, !!active && (this.keys.has(active.key) || rowFits));
  }

  paintBadge(active, fits) {
    if (!this.badge) return;
    const text = this.badge.querySelector('[data-fitment-badge-text]') || this.badge;
    // Hidden rather than negative: see the note at the top of this file.
    if (!active || !fits) {
      this.badge.hidden = true;
      text.textContent = '';
    } else {
      text.textContent = `Guaranteed to fit your ${active.nickname || active.label}`;
      this.badge.hidden = false;
    }
  }
}

if (!customElements.get('product-fitment')) {
  customElements.define('product-fitment', ProductFitment);
}
